/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Two variants, one set of consent semantics.
 *
 * The BANNER (default) is a one-line bar. It used to be a card: a 56px icon
 * tile, a heading, a four-line paragraph and two large buttons, pinned to the
 * bottom — which covered both hero buttons on a laptop and roughly half the
 * first screen on a phone, on the visit where a first-time reader decides
 * whether this site is worth their time. The decision it asks for is one
 * sentence long, so the bar is one sentence tall. Copy for it lives under its
 * own key (`cookies.banner_short`): the long explanation stayed where it
 * belongs, on the wall variant and the cookie policy.
 *
 * The WALL (opt-in, `cookiewallEnabled`) is unchanged — a real modal with the
 * full text, focus management and Escape-to-reject.
 *
 * Nothing about consent changed: the same two handlers, the same gtag update
 * order (decision first, event second), the same recorded decision behind the
 * X, and no CONSENT_VERSION bump — this is layout, and a re-consent prompt for
 * a layout change would be noise.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCookies } from '../contexts/CookieContext';
import { m, AnimatePresence } from 'motion/react';
import { Cookie, X, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SURFACES, trackEvent } from '../lib/gtag';

/** The bar's height on <html>, for what else is fixed to the bottom edge (see below). */
const clearCookieBarHeight = () =>
  document.documentElement.style.removeProperty('--cookie-bar-height');

export const CookieBanner: React.FC = () => {
  const { language, t } = useLanguage();
  const { hasConsent, acceptAll, acceptNecessary, cookiewallEnabled } = useCookies();
  const [isVisible, setIsVisible] = useState(false);
  const rejectButtonRef = useRef<HTMLButtonElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // The bar is fixed, so the page needs an in-flow spacer or its last content
  // (a contact submit, a province form) sits under it with no way to scroll
  // past. That spacer used to be three hardcoded heights that were wrong in
  // every language; it now measures the bar it is compensating for.
  const [barHeight, setBarHeight] = useState(0);

  useEffect(() => {
    if (!hasConsent) {
      // Cookiewall: show immediately with no delay; banner: show after 1.5s
      const timer = setTimeout(() => setIsVisible(true), cookiewallEnabled ? 0 : 1500);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [hasConsent, cookiewallEnabled]);

  useEffect(() => {
    const node = barRef.current;
    if (!isVisible || cookiewallEnabled || !node) return;
    // The measured height is also published as `--cookie-bar-height` on
    // <html>, so anything else fixed to the bottom edge (the comparer's
    // shortlist tray, src/components/comparison/ShortlistTray.tsx) can sit
    // above the bar instead of behind it. Client-only in both directions —
    // nothing of it is baked.
    const root = document.documentElement;
    const measure = () => {
      const height = node.offsetHeight;
      setBarHeight(height);
      root.style.setProperty('--cookie-bar-height', `${height}px`);
    };
    measure();
    // Rotation, a font swap and a language switch all change the wrap.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      // A bar that is animating out is still in the document: it keeps the
      // variable until its exit has completed (onExitComplete below), so the
      // tray does not drop behind it mid-animation. A language switch re-runs
      // this effect and re-measures at once; a bar that left the document
      // with the banner (unmounted, or the wall variant took over) clears it
      // here.
      if (!node.isConnected) clearCookieBarHeight();
    };
  }, [isVisible, cookiewallEnabled, language]);

  // The wall variant claims aria-modal, so it must also behave like a modal:
  // focus moves into it on open (onto the least-privilege choice) and Escape
  // records a "necessary only" decision — same semantics as the banner's X.
  //
  // The WALL only. The bottom banner is not modal, and an Escape pressed
  // anywhere on the page used to record "necessary only" for a year — most
  // often the Escape that closed the kWh estimator or the MID checker, both
  // of which listen for the same key: one keystroke, two unrelated decisions.
  useEffect(() => {
    if (!isVisible || !cookiewallEnabled) return;
    rejectButtonRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleReject();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, cookiewallEnabled]);

  // Decision first, tracking second: persist() pushes the gtag consent update
  // synchronously, so the cookie_consent event below already runs under the
  // just-made decision (an accept used to be dispatched still-denied).
  const handleAccept = () => {
    acceptAll();
    trackEvent('cookie_consent', {
      decision: 'accept',
      surface: SURFACES.cookieBanner,
      variant: cookiewallEnabled ? 'wall' : 'banner',
      language,
    });
  };

  const handleReject = () => {
    acceptNecessary();
    trackEvent('cookie_consent', {
      decision: 'reject',
      surface: SURFACES.cookieBanner,
      variant: cookiewallEnabled ? 'wall' : 'banner',
      language,
    });
  };

  const policyLink = (
    <Link to={`/${language}/cookies`} className="text-emerald-500 hover:underline font-bold">
      {t('cookies.title')}
    </Link>
  );

  if (cookiewallEnabled) {
    return (
      <AnimatePresence>
        {isVisible && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/90 md:backdrop-blur-md"
            role="dialog"
            aria-modal="true"
            aria-label={t('cookies.banner_title')}
          >
            <m.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-2xl"
            >
              <section
                aria-label={t('cookies.banner_title')}
                className="bg-slate-800/95 md:backdrop-blur-xl border-2 border-emerald-500/20 rounded-3xl shadow-2xl shadow-slate-900/50 p-6 md:p-8 flex flex-col lg:flex-row items-center gap-6 lg:gap-12 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-900/20 rounded-full blur-3xl -z-10 opacity-30 translate-x-1/2 -translate-y-1/2" />

                <div className="flex items-center gap-4 shrink-0">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-900/30 flex items-center justify-center shrink-0">
                    <Cookie className="w-8 h-8 text-emerald-500" />
                  </div>
                  <div className="lg:max-w-md">
                    <h2 className="text-xl font-black text-white mb-1">
                      {t('cookies.banner_title')}
                    </h2>
                    <p className="text-sm text-slate-400 font-medium leading-relaxed">
                      {t('cookies.banner_text')} {policyLink}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:ml-auto">
                  <button
                    ref={rejectButtonRef}
                    onClick={handleReject}
                    className="flex-1 lg:flex-none px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-black rounded-xl transition-all focus-ring"
                  >
                    {t('cookies.reject_all')}
                  </button>
                  <button
                    onClick={handleAccept}
                    className="flex-1 lg:flex-none px-8 py-3 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-black rounded-xl transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2 focus-ring"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {t('cookies.accept_all')}
                  </button>
                </div>
              </section>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence onExitComplete={clearCookieBarHeight}>
      {isVisible && (
        <React.Fragment key="cookie-banner">
          {/* Measured, not guessed — see barHeight above. The fallback covers
              the first paint before the observer reports. */}
          <div aria-hidden="true" style={{ height: barHeight || 96 }} />
          <m.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-[100] px-3 pb-3 sm:px-4 sm:pb-4"
            role="region"
            aria-label={t('cookies.banner_title')}
            ref={barRef}
          >
            <section
              aria-label={t('cookies.banner_title')}
              className="max-w-5xl mx-auto bg-slate-800 lg:bg-slate-800/95 lg:backdrop-blur-xl border border-emerald-500/25 rounded-2xl shadow-2xl shadow-slate-900/50 px-4 py-3 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
            >
              <Cookie
                className="w-5 h-5 text-emerald-500 shrink-0 hidden sm:block"
                aria-hidden="true"
              />
              <p className="text-[13px] text-slate-300 [html.light_&]:text-[#334155] font-medium leading-snug flex-1 min-w-0">
                {t('cookies.banner_short')} {policyLink}
              </p>
              {/* Below sm the two labels cannot share a row at 390px — they
                  stack full-width instead of the accept button running off
                  the screen edge. */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 shrink-0">
                <button
                  onClick={handleReject}
                  className="w-full sm:w-auto px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-[13px] font-black rounded-lg transition-all focus-ring whitespace-nowrap"
                >
                  {t('cookies.reject_all')}
                </button>
                <button
                  onClick={handleAccept}
                  className="w-full sm:w-auto px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-[13px] font-black rounded-lg transition-all flex items-center justify-center gap-1.5 focus-ring whitespace-nowrap"
                >
                  <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                  {t('cookies.accept_all')}
                </button>
                {/* X dismiss = explicit "reject all" so the choice is persisted
                    and we don't strand visitors at default-denied without a
                    recorded consent decision. */}
                <button
                  onClick={handleReject}
                  aria-label={t('cookies.reject_all')}
                  className="hidden sm:block p-1.5 text-fg-muted hover:text-slate-300 transition-colors focus-ring rounded-lg shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </section>
          </m.div>
        </React.Fragment>
      )}
    </AnimatePresence>
  );
};
